/* eslint-disable react-refresh/only-export-components -- context object and its consumer hooks must coexist in this file */
import * as React from 'react';

type BreadcrumbLabelsContextValue = {
  labelsById: Record<string, string>;
  setLabel: (id: string, name: string) => void;
  clearLabel: (id: string) => void;
};

const BreadcrumbLabelsContext = React.createContext<BreadcrumbLabelsContextValue>({
  labelsById: {},
  setLabel: () => {},
  clearLabel: () => {},
});

export function BreadcrumbLabelsProvider({ children }: Readonly<{ children: React.ReactNode }>) {
  const [labelsById, setLabelsById] = React.useState<Record<string, string>>({});

  const setLabel = React.useCallback((id: string, name: string) => {
    setLabelsById((current) => (current[id] === name ? current : { ...current, [id]: name }));
  }, []);

  const clearLabel = React.useCallback((id: string) => {
    setLabelsById((current) => {
      if (!Object.hasOwn(current, id)) return current;
      const rest = { ...current };
      delete rest[id];
      return rest;
    });
  }, []);

  const value = React.useMemo(
    () => ({ labelsById, setLabel, clearLabel }),
    [labelsById, setLabel, clearLabel],
  );

  return (
    <BreadcrumbLabelsContext.Provider value={value}>{children}</BreadcrumbLabelsContext.Provider>
  );
}

export function useBreadcrumbLabels(): BreadcrumbLabelsContextValue {
  return React.useContext(BreadcrumbLabelsContext);
}

/** Registers `name` as the breadcrumb label for the id-shaped URL segment `id`, for as long as
 * the calling component stays mounted. Call with the loaded entity's own id and display name —
 * both undefined (still loading) is a no-op, so the segment keeps showing its generic fallback
 * until the real name is available. */
export function useBreadcrumbLabel(id: string | undefined, name: string | undefined): void {
  const { setLabel, clearLabel } = useBreadcrumbLabels();

  // WHY: useLayoutEffect avoids a one-frame fallback-label flash when the name is already cached.
  React.useLayoutEffect(() => {
    if (!id || !name) return;
    setLabel(id, name);
    return () => clearLabel(id);
  }, [id, name, setLabel, clearLabel]);
}
